/* Update cart items */
if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();

      this.giftSelector = document.querySelector('gift-selector');
      this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');

      this.debouncedOnChange = debounce((event) => {
        // Ignore the change for some fields
        if (
          event.target != document.getElementById('Cart-note') &&
          event.target != document.getElementById('apply-discount-btn') &&
          event.target != document.getElementById('discount-code-input') &&
          event.target != document.getElementById('accept-privacy-policy') &&
          event.target != document.querySelector('[data-discount-code-input]') &&
          event.target != document.querySelector('[data-apply-discount-btn]') &&
          event.target != document.getElementById('apply-discount-btn')
        ) {
          this.onChange(event);
        }
      }, 200);

      this.addEventListener('change', this.debouncedOnChange.bind(this));
    }

    onChange(event) {
      this.updateQuantity(
        event.target.dataset.index,
        event.target.value,
        document.activeElement.getAttribute('name'),
        event
      );
    }

    getSectionsToRender() {
      return [
        {
          id: 'main-cart-items',
          section: document.getElementById('main-cart-items').dataset.id,
          selector: '.js-cart-item-contents',
        },
        {
          id: 'main-cart-footer',
          section: document.getElementById('main-cart-footer').dataset.id,
          selector: '.js-cart-footer-contents',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '[data-cart-icon-bubble]',
        },
      ];
    }

    /**
     * @method updateQuantity
     * @description Works together with the Ajax Cart API.
     * @see https://shopify.dev/docs/api/ajax/reference/cart
     * @param {Number} line
     * @param {Number} quantity
     * @param {String} name
     * @param {Object} event
     */
    async updateQuantity(line, quantity, name, event) {
      this.enableLoading();

      try {
        // Create the body for the request
        const body = JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });

        // Fetch POST to the cart change endpoint
        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body,
        });

        // Return the in JSON format
        let parsedState = await response.json();

        // Search for elements in the DOM
        const items = document.querySelectorAll('.cart-item');
        let quantityElement = document.getElementById(`Quantity-${line}`);

        // Check if there are errors in the response
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Tell the user that something is wrong when there are no sections returned in the response.
        if (!parsedState.sections) {
          throw new Error();
        }

        // Check if there is a gift in the cart and remove it if the total price is lower than the gift tier.
        if (this.isThereAGiftInCart(parsedState)) {
          parsedState = await this.checkGiftInCart(parsedState);
        }

        // Toggle UI state based on cart quantity
        this.classList.toggle('cart-is-empty', parsedState.item_count === 0);
        const item_count_target = this.querySelector('[data-cart-items="item_count"]');
        if (item_count_target) {
          item_count_target.textContent = parsedState.item_count;
        }

        // Update UI with fetched HTML returned in response.sections
        this.renderContents(parsedState);
        this.callCartUpdatedEvent(parsedState);

        // Check if the updated value is set if not then the max amount for that product is reached

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = '';

        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(document.getElementById(`Quantity-${line}`).value)
        ) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        document.getElementById(`cartItem-${line}`)?.querySelector(`[name="${name}"]`)?.focus();
      } catch (error) {
        console.log(error);
        // Show the general error message that something went wrong to the users
        const errorMessageTarget = document.getElementById('cart-errors');
        errorMessageTarget.textContent = window.cartStrings.error;
      } finally {
        this.disableLoading();
        // Let the chat bot know that the cart has been changed
        if ('Zowie' in window) {
          Zowie.updateCart();
        }
      }
    }

    /**
     * @method callCartUpdatedEvent
     * @param {Object} parsedState
     */
    callCartUpdatedEvent(parsedState) {
      // Dispatch event for other components to pick up on
      const cartUpdateEvent = new CustomEvent('cart-update', {
        bubbles: true,
        detail: {
          ...parsedState,
        },
      });
      document.dispatchEvent(cartUpdateEvent);
    }

    /**
     * @method checkGiftInCart
     * @description Delete the gift(s) from the cart when the total price is lower than the gift tier.
     * @param {Object} parsedState the current cart json object response.
     * @returns {Object} the same or updated cart json object response.
     */
    async checkGiftInCart(parsedState) {
      const itemsToDelete = parsedState?.items.filter((line_item) => {
        // Check if the line item has the gift property
        if ('_gift' in line_item.properties == false) return false;

        // Check if this line_item gift has the discount applied from the shopify functions. If so don't delete the product, because the gift is allowed.
        return line_item?.total_discount && line_item.price === line_item.total_discount
          ? false
          : line_item;
      });

      // Check if we need to delete products from the cart, if not return the given parsedState parameter
      if (!itemsToDelete.length) return parsedState;

      let cartState = parsedState;

      // Delete all gifts per item
      for (const line_item of itemsToDelete) {
        const body = JSON.stringify({
          id: line_item.key,
          quantity: 0,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: body,
        });

        cartState = await response.json();
      }

      return cartState;
    }

    /**
     * @method isThereAGiftInCart
     * @description Check if the tier gift is in the cart.
     * @param {Object} parsedState the cart json object response.
     * @returns {Boolean} true or false.
     */
    isThereAGiftInCart(parsedState) {
      return parsedState.items.some((line_item) => '_gift' in line_item.properties);
    }

    /**
     * @method renderContents
     * @description Replaces the current DOM with updated HTML from parsedState response.
     * @param {Object} parsedState
     */
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) ||
          document.getElementById(section.id);

        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });
    }

    /**
     * @method updateLiveRegions
     * @description Show error texts, should not be deleted.
     * @param {Number} line
     * @param {Number} message
     */
    updateLiveRegions(line, message) {
      // Check if we can find the line item error element based on line index, if so show the error in line item context
      const lineItemError = document.getElementById(`Line-item-error-${line}`);
      if (lineItemError) {
        lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
      }

      // Update attribute from the lineItemStatusElement
      this.lineItemStatusElement.setAttribute('aria-hidden', true);

      // Update the cart live region text with the correct attribute
      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    enableLoading() {
      this.querySelector('[data-cart-items="submit"]')?.classList?.add('loading');
      this.classList.add('cart-items-is-loading');
      document.querySelector('[data-cart-loader-active="show"]')?.classList?.remove('hidden');
      document.querySelector('[data-cart-loader-active="hidden"]')?.classList?.add('hidden');
      document.activeElement.blur();
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }

    disableLoading() {
      this.querySelector('[data-cart-items="submit"]')?.classList?.remove('loading');
      document.querySelector('[data-cart-loader-active="show"]')?.classList?.add('hidden');
      document.querySelector('[data-cart-loader-active="hidden"]')?.classList?.remove('hidden');
      document.querySelector('cart-items')?.classList?.remove('cart-items-is-loading');
    }

    /**
     * refreshState
     * @description Fetches and replaces the cart-items web component completly.
     */
    async refreshState() {
      if (!document.querySelector('cart-items')) return;

      try {
        const [cart, sections] = await Promise.all([
          fetch(`${routes.cart_url}.js`).then((response) => response.json()),
          fetch(
            `?sections=${this.getSectionsToRender()
              .map((section) => section.section)
              .join(',')}`
          ).then((response) => response.json()),
        ]);

        let formatParsedState = {
          ...cart,
          sections: sections,
        };

        if (this.isThereAGiftInCart(formatParsedState)) {
          formatParsedState = await this.checkGiftInCart(formatParsedState);
        }

        // Update UI with fetched HTML returned in response.sections
        this.renderContents(formatParsedState);
        this.callCartUpdatedEvent(formatParsedState);

        // Set correct is gift tier in cart state
        if (this.giftSelector) {
          this.isThereAGiftInCart(formatParsedState) == false
            ? this.giftSelector.setAttribute('gift', 'not-in-cart')
            : this.giftSelector.setAttribute('gift', 'in-cart');
        }
      } catch (error) {
        console.log(error);
      }
    }
  }
  customElements.define('cart-items', CartItems);
}

if (!customElements.get('cart-remove-button')) {
  /**
   * CartRemoveButton
   * @description Remove item from the cart.
   */
  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.closest('cart-items').updateQuantity(this.dataset.index, 0);
      });
    }
  }
  customElements.define('cart-remove-button', CartRemoveButton);
}

if (!customElements.get('cart-note')) {
  /**
   * CartNote
   * @description Update cart note.
   */
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener(
        'change',
        debounce((event) => {
          const body = JSON.stringify({ note: event.target.value });
          fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        }, 300)
      );
    }
  }
  customElements.define('cart-note', CartNote);
}
